import { useSearchParams } from 'react-router-dom';

import { safeSignInRedirectUrl } from '@/config/clerk-satellite';

/**
 * Read `redirect_url` from both the query string AND the URL hash and return it
 * only if the origin is in the allowlist of satellite apps (partner whitelabels
 * + TagadaPay-owned satellites like dashboard.tagadapay.io, paydash.tagada.io).
 * This is what lets a satellite's /sign-in bounce here and get the user returned
 * to the satellite host after sign-in (the Clerk satellite cookie handshake
 * carries the session back).
 *
 * Why both? Clerk's AccountPortal-style URL builder emits
 * `/sign-in#/?redirect_url=...` (everything after `?` lives in the fragment, not
 * in `location.search`). `useSearchParams` ONLY reads the query string, so
 * without explicit hash parsing the partner intent is silently dropped on the
 * floor — the user signs in, lands on `/`, and on the next visit to the
 * satellite gets redirected back here in a loop because the satellite never
 * receives a handshake.
 */
export function useSafeRedirectUrl(): string | null {
  const [searchParams] = useSearchParams();
  const fromQuery = searchParams.get('redirect_url');
  if (fromQuery) return safeSignInRedirectUrl(fromQuery);

  if (typeof window !== 'undefined' && window.location.hash) {
    // Hash typically looks like `#/?redirect_url=...&sign_in_force_redirect_url=...`
    // strip the leading `#` (and an optional `/`) and parse what's left.
    const hash = window.location.hash.replace(/^#\/?/, '');
    const qIdx = hash.indexOf('?');
    const query = qIdx >= 0 ? hash.slice(qIdx + 1) : hash;
    try {
      const params = new URLSearchParams(query);
      const fromHash = params.get('redirect_url');
      if (fromHash) return safeSignInRedirectUrl(fromHash);
    } catch {
      // ignore, fall through to null
    }
  }

  return null;
}

/**
 * Same-origin CRM deep-link return path (e.g. billing promo activation).
 * Only accepts relative paths starting with `/` (no protocol-relative / open redirects).
 */
export function useSafeReturnToPath(): string | null {
  const [searchParams] = useSearchParams();
  const raw = searchParams.get('return_to');
  if (!raw) return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  return raw;
}
